import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Outlet, Link } from 'react-router-dom'; 
import { motion, AnimatePresence } from 'framer-motion'; 
import { Menu, Globe, Heart, ShoppingBag, User, ChevronDown, ChevronRight, Facebook, Instagram, Linkedin } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { useLanguageStore, useAuthStore, useQuoteStore, useWishlistStore, useAppearanceStore, getLocalized } from '../store';
import { useTranslation } from '../i18n/translations';
import { LogoWordmark } from './LogoWordmark'; 

const API = process.env.REACT_APP_BACKEND_URL || '';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'nl', name: 'Nederlands' },
  { code: 'ro', name: 'Română' },
  { code: 'ru', name: 'Русский' },
];

const STATIC_HEADER_CATEGORIES = [
  { id: "kitchens", name: { en: "Kitchens", nl: "Keukens", ro: "Bucătării", ru: "Кухни" } },
  { id: "wardrobes", name: { en: "Wardrobes", nl: "Kasten / Garderobes", ro: "Garderobe", ru: "Шкафы" } },
  { id: "doors", name: { en: "Doors", nl: "Deuren", ro: "Uși", ru: "Двери" }, subcategories: [
    { id: "invisible_doors", name: { en: "Invisible Doors", nl: "Onzichtbare Deuren", ro: "Uși filomuro", ru: "Скрытые Двери" } },
    { id: "sliding_partitions", name: { en: "Sliding Partitions", nl: "Schuifwanden", ro: "Partiții glisante", ru: "Раздвижные перегородки" } },
    { id: "pivot_doors", name: { en: "Pivot Doors", nl: "Pivotdeuren", ro: "Uși pivotante", ru: "Поворотные двери" } }
  ] },
  { id: "wall_panels", name: { en: "Wall Panels", nl: "Wandpanelen", ro: "Panouri de Perete", ru: "Стеновые панели" } },
  { id: "soft_furniture", name: { en: "Soft Furniture", nl: "Zachte Meubels", ro: "Mobilier Tapițat", ru: "Мягкая мебель" } },
  { id: "ready_solutions", name: { en: "Ready Solutions", nl: "Kant-en-klare Oplossingen", ro: "Soluții Gata", ru: "Готовые решения" } },
  { id: "custom_furniture", name: { en: "Custom Furniture", nl: "Maatwerk Meubels", ro: "Mobilier la Comandă", ru: "Мебель на заказ" } }
];

let globalHeaderCategories = STATIC_HEADER_CATEGORIES;

const toSafeStr = (val, fallback = '') => {
  if (val === null || val === undefined) return fallback;
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    const firstStr = Object.values(val).find((v) => typeof v === 'string');
    return firstStr ? String(firstStr) : fallback;
  }
  return String(val);
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  
  const { language, setLanguage } = useLanguageStore();
  const { t } = useTranslation(language);
  const { isAuthenticated, user, logout } = useAuthStore();
  const { items: quoteItems } = useQuoteStore();
  const { items: wishlistItems } = useWishlistStore();
  const location = useLocation();
  const navigate = useNavigate();

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const logo = useAppearanceStore((s) => s.config?.logo || {});

  const [categories, setCategories] = useState(globalHeaderCategories);

  useEffect(() => {
    fetch(`${API}/api/products/categories/list`)
      .then((res) => res.json())
      .then((data) => {
        const fetchedCats = data?.categories || [];
        if (Array.isArray(fetchedCats) && fetchedCats.length > 0) {
          globalHeaderCategories = fetchedCats;
          setCategories(fetchedCats);
        }
      })
      .catch((err) => console.error('Failed to load dynamic categories in layout navigation:', err));
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/^\/([a-z]{2})/);
    if (match && match[1]) {
      const urlLang = match[1];
      const isSupported = LANGUAGES.some((l) => l.code === urlLang);
      if (isSupported && urlLang !== language) {
        setLanguage(urlLang);
      }
    }
  }, [location.pathname, language, setLanguage]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setIsMegaMenuOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  const isB2BApproved = isAuthenticated && user?.is_b2b && user?.is_approved;

  const navLinks = [
    { href: `/${language}/products`, label: toSafeStr(t?.nav?.products, 'Products'), hasMegaMenu: true, id: 'products' },
    { href: `/${language}/about`, label: toSafeStr(t?.nav?.about, 'About'), id: 'about' },
    { href: `/${language}/projects`, label: toSafeStr(t?.nav?.projects, 'Projects'), id: 'projects' },
    { href: `/${language}/blog`, label: toSafeStr(t?.nav?.blog, 'Blog'), id: 'blog' },
    { href: `/${language}/b2b`, label: toSafeStr(t?.nav?.b2b, 'Trade'), id: 'b2b' },
    ...(isB2BApproved ? [{ href: `/${language}/b2b/downloads`, label: toSafeStr(t?.b2b?.downloadCenter, 'Downloads'), id: 'b2b-downloads' }] : []),
    { href: `/${language}/contact`, label: toSafeStr(t?.nav?.contact, 'Contact'), id: 'contact' },
  ];

  const handleSpecificationNavigation = () => {
    if (isB2BApproved) {
      navigate(`/${language}/b2b/dashboard`);
    } else {
      navigate(`/${language}/quote`);
    }
  };

  const renderHeaderLogo = () => {
    if (logo?.mode === 'image' && logo?.header_image_url) {
      const scaleFactor = logo.image_scale ? (logo.image_scale > 5 ? logo.image_scale / 100 : logo.image_scale) : 1;
      return (
        <img 
          src={logo.header_image_url} 
          alt={toSafeStr(logo.text, 'FEDYK Furniture')} 
          className="w-auto object-contain transition-all duration-200 select-none" 
          style={{ height: `${scaleFactor * 2.25}rem` }}
          data-testid="logo-image"
          draggable={false}
        />
      );
    }
    return <LogoWordmark logo={logo} testid="logo-text" />;
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 select-none ${
        isScrolled || isMegaMenuOpen ? 'bg-background/95 backdrop-blur-md border-b border-border' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          <div 
            onClick={() => navigate(`/${language}${location.search}`)} 
            className="flex items-center cursor-pointer select-none" 
            data-testid="logo"
          >
            {renderHeaderLogo()}
          </div>

          {/* ДЕСКТОПНАЯ НАВИГАЦИЯ */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <div key={link.href} className="relative">
                {link.hasMegaMenu ? (
                  <button
                    type="button"
                    onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
                    className="flex items-center gap-1 text-sm tracking-wide hover:text-muted-foreground transition-colors font-light uppercase select-none"
                    data-testid="products-nav-btn"
                  >
                    {link.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${isMegaMenuOpen ? 'rotate-180' : ''}`} />
                  </button>
                ) : (
                  <Link
                    to={`${link.href}${location.search}`}
                    className="text-sm tracking-wide hover:text-muted-foreground transition-colors cursor-pointer select-none block font-light uppercase"
                    data-testid={`nav-${link.id}`}
                  >
                    {link.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-4 select-none">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="language-switcher">
                  <Globe className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                {LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code);
                      const newPath = location.pathname.replace(/^\/[a-z]{2}/, `/${lang.code}`) + location.search;
                      navigate(newPath || `/${lang.code}`);
                    }}
                    className={`rounded-none text-xs cursor-pointer select-none ${language === lang.code ? 'bg-accent' : ''}`}
                    data-testid={`lang-${lang.code}`}
                  >
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div onClick={() => navigate(`/${language}/wishlist`)} className="cursor-pointer" data-testid="wishlist-link">
              <Button variant="ghost" size="icon" className="h-10 w-10 relative pointer-events-none">
                <Heart className="w-5 h-5" strokeWidth={1.5} />
                {wishlistItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-foreground text-background text-xs flex items-center justify-center">
                    {wishlistItems.length}
                  </span>
                )}
              </Button>
            </div>

            <div onClick={handleSpecificationNavigation} className="cursor-pointer" data-testid="quote-link">
              <Button variant="ghost" size="icon" className="h-10 w-10 relative pointer-events-none">
                <ShoppingBag className="w-5 h-5" strokeWidth={1.5} />
                {quoteItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-foreground text-background text-xs flex items-center justify-center">
                    {quoteItems.length}
                  </span>
                )}
              </Button>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-10 w-10" data-testid="user-menu">
                  <User className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                {isAuthenticated ? (
                  <>
                    <DropdownMenuItem className="rounded-none text-xs cursor-pointer" onClick={() => navigate(`/${language}/account`)}>
                      {toSafeStr(t?.auth?.myAccount, 'My Account')}
                    </DropdownMenuItem>
                    {user?.is_b2b && user?.is_approved && (
                      <DropdownMenuItem className="rounded-none text-xs cursor-pointer" onClick={() => navigate(`/${language}/b2b/dashboard`)}>
                        {toSafeStr(t?.b2bDashboard?.title, 'B2B Dashboard')}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      className="rounded-none text-xs cursor-pointer"
                      onClick={() => {
                        navigate(`/${language}`);
                        logout();
                      }} 
                      data-testid="logout-btn"
                    >
                      {toSafeStr(t?.auth?.logout, 'Logout')}
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem className="rounded-none text-xs cursor-pointer" onClick={() => navigate(`/${language}/login`)} data-testid="login-link">
                      {toSafeStr(t?.auth?.login, 'Login')}
                    </DropdownMenuItem>
                    <DropdownMenuItem className="rounded-none text-xs cursor-pointer" onClick={() => navigate(`/${language}/register`)} data-testid="register-link">
                      {toSafeStr(t?.auth?.register, 'Register')}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* МОБИЛЬНАЯ ШТОРКА / МЕНЮ */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden h-10 w-10" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full max-w-md rounded-none overflow-y-auto">
                <nav className="flex flex-col gap-2 mt-8 text-left select-none">
                  {navLinks.map((link) => {
                    if (link.hasMegaMenu) {
                      return (
                        <div key={link.href} className="border-b border-border py-2">
                          <button
                            type="button"
                            onClick={() => setMobileProductsOpen(!mobileProductsOpen)}
                            className="w-full flex items-center justify-between text-lg font-heading tracking-wide uppercase font-light cursor-pointer text-left"
                          >
                            <span>{link.label}</span>
                            <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
                          </button>

                          {/* ВЫПАДАЮЩИЙ СПИСОК КАТЕГОРИЙ БЕЗ КАРТИНОК ДЛЯ МОБИЛЬНЫХ */}
                          <AnimatePresence>
                            {mobileProductsOpen && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden pl-3 pt-3 space-y-2"
                              >
                                {categories.map((cat) => {
                                  const categoryLocalizedName = toSafeStr(getLocalized(cat.name, language) || t?.categories?.[cat.id], cat.id);
                                  return (
                                    <div key={cat.id} className="space-y-1">
                                      <div
                                        onClick={() => {
                                          setMobileOpen(false);
                                          navigate(cat.id === 'custom_furniture' ? `/${language}/custom-furniture` : `/${language}/products?category=${cat.id}`);
                                        }}
                                        className="text-sm font-light text-foreground/90 hover:text-foreground cursor-pointer uppercase tracking-wider py-1 flex items-center justify-between"
                                      >
                                        <span>{categoryLocalizedName}</span>
                                      </div>

                                      {/* ПОДКАТЕГОРИИ (ЕСЛИ ЕСТЬ) */}
                                      {cat.subcategories && cat.subcategories.length > 0 && (
                                        <div className="pl-3 space-y-1 border-l border-border/50 my-1">
                                          {cat.subcategories.map((sub) => {
                                            const subLocalizedName = toSafeStr(getLocalized(sub.name, language), sub.id);
                                            return (
                                              <div
                                                key={sub.id}
                                                onClick={() => {
                                                  setMobileOpen(false);
                                                  navigate(`/${language}/products?category=${cat.id}&subcategory=${sub.id}`);
                                                }}
                                                className="text-xs text-muted-foreground hover:text-foreground cursor-pointer py-1 font-light"
                                              >
                                                {subLocalizedName}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={link.href}
                        onClick={() => {
                          setMobileOpen(false);
                          navigate(`${link.href}${location.search}`);
                        }}
                        className="text-lg font-heading tracking-wide py-3 border-b border-border cursor-pointer uppercase font-light"
                      >
                        {link.label}
                      </div>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* МЕГА-МЕНЮ ДЛЯ ДЕСКТОПА */}
      <AnimatePresence>
        {isMegaMenuOpen && categories.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 z-50 bg-background border-b border-border shadow-lg select-none"
            onMouseLeave={() => setIsMegaMenuOpen(false)}
            data-testid="mega-menu"
          >
            <div className="container-custom py-12 text-left">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-6">
                {categories.map((cat) => {
                  const megaMenuImgObj = siteImages[`megamenu_${cat.id}`] || siteImages[`mega_menu_${cat.id}`] || siteImages[cat.id];
                  let megaMenuImgUrl = megaMenuImgObj?.url || megaMenuImgObj;

                  if (megaMenuImgUrl && typeof megaMenuImgUrl === 'string' && megaMenuImgUrl.includes('/upload/')) {
                    megaMenuImgUrl = megaMenuImgUrl.replace('/upload/', '/upload/w_500,q_auto,f_auto/');
                  }

                  const categoryLocalizedName = toSafeStr(getLocalized(cat.name, language) || t?.categories?.[cat.id], cat.id);

                  return (
                    <div key={cat.id} data-testid={`mega-menu-${cat.id}`} className="text-center">
                      <div
                        onClick={() => {
                          setIsMegaMenuOpen(false);
                          navigate(cat.id === 'custom_furniture' ? `/${language}/custom-furniture` : `/${language}/products?category=${cat.id}`);
                        }}
                        className="group block cursor-pointer"
                      >
                        <div className="aspect-square overflow-hidden bg-gradient-to-br from-muted to-foreground/5 mb-4 flex items-center justify-center border border-border/40 rounded-none">
                          {megaMenuImgUrl ? (
                            <img
                              src={megaMenuImgUrl}
                              alt={categoryLocalizedName}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03] select-none pointer-events-none"
                              data-testid={`mega-menu-img-${cat.id}`}
                              draggable={false}
                            />
                          ) : (
                            <span className="text-xs text-muted-foreground font-medium tracking-wide text-center px-2">
                              {categoryLocalizedName}
                            </span>
                          )}
                        </div>
                        <p className="text-sm font-medium tracking-wide text-center group-hover:text-muted-foreground transition-colors uppercase font-light">{categoryLocalizedName}</p>
                      </div>
                      
                      {cat.subcategories && cat.subcategories.length > 0 && (
                        <div className="mt-3 space-y-1.5 text-center">
                          {cat.subcategories.map((sub) => {
                            const subcategoryLocalizedName = toSafeStr(getLocalized(sub.name, language), sub.id);
                            return (
                              <div
                                key={sub.id}
                                onClick={() => {
                                  setIsMegaMenuOpen(false);
                                  navigate(`/${language}/products?category=${cat.id}&subcategory=${sub.id}`);
                                }}
                                className="block text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                                data-testid={`mega-menu-sub-${sub.id}`}
                              >
                                {subcategoryLocalizedName}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export const Footer = ({ cmsSettings }) => {
  const { language, setLanguage } = useLanguageStore();
  const { t } = useTranslation(language);
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const appearanceConfig = useAppearanceStore((s) => s.config || {});
  const logo = appearanceConfig.logo || {};

  const brandEmail = appearanceConfig.brand_email || 
                     appearanceConfig.contact_email || 
                     appearanceConfig.email || 
                     cmsSettings?.brand_email || 
                     cmsSettings?.contact_email || 
                     cmsSettings?.email || 
                     process.env.REACT_APP_BRAND_EMAIL || 
                     'info@fedykfurniture.com';

  const socials = [
    { key: 'facebook', Icon: Facebook, url: appearanceConfig.facebook_url, enabled: appearanceConfig.facebook_enabled },
    { key: 'instagram', Icon: Instagram, url: appearanceConfig.instagram_url, enabled: appearanceConfig.instagram_enabled },
    { key: 'linkedin', Icon: Linkedin, url: appearanceConfig.linkedin_url, enabled: appearanceConfig.linkedin_enabled },
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription failed:', error);
    }
  };

  const renderFooterLogo = () => {
    if (logo?.mode === 'image' && (logo?.footer_image_url || logo?.header_image_url)) {
      const scaleFactor = logo.image_scale ? (logo.image_scale > 5 ? logo.image_scale / 100 : logo.image_scale) : 1;
      return (
        <img 
          src={logo.footer_image_url || logo.header_image_url} 
          alt={toSafeStr(logo.text, 'FEDYK Furniture')} 
          className="w-auto object-contain transition-all duration-200 select-none" 
          style={{ height: `${scaleFactor * 2.25}rem` }}
          data-testid="footer-logo-image" 
          draggable={false}
        />
      );
    }
    return <LogoWordmark logo={logo} className="font-heading text-2xl tracking-tight text-white" testid="footer-logo-text" />;
  };

  return (
    <footer className="bg-foreground text-background text-left font-light select-none" data-testid="footer">
      <div className="border-b border-background/10">
        <div className="container-custom section-padding">
          <div className="max-w-2xl">
            <h2 className="text-h2 mb-4 text-white font-extralight tracking-tight uppercase">{toSafeStr(t?.newsletter?.title, 'Newsletter')}</h2>
            <p className="text-body-lg text-background/60 mb-8">{toSafeStr(t?.newsletter?.subtitle, 'Subscribe for updates')}</p>
            {subscribed ? (
              <p className="text-background/80">{toSafeStr(t?.newsletter?.success, 'Thank you for subscribing!')}</p>
            ) : (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full max-w-xl">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={toSafeStr(t?.newsletter?.placeholder, 'Enter your email')}
                  className="w-full sm:flex-1 min-h-12 h-auto py-3 px-4 bg-transparent border border-background/20 text-background placeholder:text-background/40 focus:outline-none focus:border-background/40 text-sm rounded-none font-light"
                  required
                  data-testid="newsletter-email"
                />
                <button 
                  type="submit" 
                  className="w-full sm:w-auto min-h-12 h-auto py-3 px-6 text-[11px] sm:text-xs font-semibold tracking-widest uppercase bg-background text-foreground hover:bg-background/90 transition-colors whitespace-normal break-words text-center rounded-none shrink-0" 
                  data-testid="newsletter-submit"
                >
                  {toSafeStr(t?.common?.subscribe, 'Subscribe')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1 space-y-6">
            <div>
              <div onClick={() => navigate(`/${language}${location.search}`)} className="inline-block mb-4 cursor-pointer">
                {renderFooterLogo()}
              </div>
              <p className="text-background/60 text-sm leading-relaxed">{toSafeStr(t?.footer?.tagline, 'Architectural furniture & interior solutions.')}</p>
            </div>
            
            <div className="flex items-center gap-4 pt-2">
              {socials.map((soc) => {
                if (soc.enabled && soc.url && soc.url.trim() !== '') {
                  return (
                    <a 
                      key={soc.key} 
                      href={soc.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-background/40 hover:text-background transition-colors p-1"
                    >
                      <soc.Icon className="w-5 h-5" strokeWidth={1.5} />
                    </a>
                  );
                }
                return null;
              })}
            </div>
          </div>

          <div>
            <h4 className="text-caption mb-6 uppercase tracking-wider text-neutral-400 font-mono text-[10px]">{toSafeStr(t?.footer?.quickLinks, 'Quick Links')}</h4>
            <ul className="space-y-3">
              <li><div onClick={() => navigate(`/${language}/products${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.products, 'Products')}</div></li>
              <li><div onClick={() => navigate(`/${language}/about${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.about, 'About')}</div></li>
              <li><div onClick={() => navigate(`/${language}/projects${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.projects, 'Projects')}</div></li>
              <li><div onClick={() => navigate(`/${language}/blog${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.blog, 'Blog')}</div></li>
            </ul>
          </div>

          <div>
            <h4 className="text-caption mb-6 uppercase tracking-wider text-neutral-400 font-mono text-[10px]">{toSafeStr(t?.footer?.support, 'Support')}</h4>
            <ul className="space-y-3">
              <li><div onClick={() => navigate(`/${language}/contact${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.contact, 'Contact')}</div></li>
              <li><div onClick={() => navigate(`/${language}/b2b${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.nav?.b2b, 'Trade')}</div></li>
              <li><a href={`mailto:${brandEmail}`} className="text-sm text-background/60 hover:text-background transition-colors font-light">{brandEmail}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-caption mb-6 uppercase tracking-wider text-neutral-400 font-mono text-[10px]">{toSafeStr(t?.footer?.legal, 'Legal')}</h4>
            <ul className="space-y-3">
              <li><div onClick={() => navigate(`/${language}/privacy${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.footer?.privacy, 'Privacy Policy')}</div></li>
              <li><div onClick={() => navigate(`/${language}/terms${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.footer?.terms, 'Terms & Conditions')}</div></li>
              <li><div onClick={() => navigate(`/${language}/cookies${location.search}`)} className="text-sm text-background/60 hover:text-background transition-colors cursor-pointer uppercase font-light">{toSafeStr(t?.footer?.cookies, 'Cookie Policy')}</div></li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-background/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-background/40 font-light">&copy; {new Date().getFullYear()} FEDYK Furniture. {toSafeStr(t?.footer?.rights, 'All rights reserved.')}</p>
          <div className="flex items-center gap-6">
            {LANGUAGES.map((lang) => (
              <div
                key={lang.code}
                onClick={() => {
                  setLanguage(lang.code);
                  const newPath = location.pathname.replace(/^\/[a-z]{2}/, `/${lang.code}`) + location.search;
                  navigate(newPath || `/${lang.code}`);
                }}
                className={`text-xs tracking-wider cursor-pointer font-light uppercase ${language === lang.code ? 'text-background' : 'text-background/40 hover:text-background/60'} transition-colors`}
              >
                {lang.code.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export const Layout = () => {
  const [cmsSettings, setCmsSettings] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/public/site-settings`)
      .then(res => res.json())
      .then(data => { if (data) setCmsSettings(data); })
      .catch(err => console.error("CMS Layout background check error:", err));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground transition-colors duration-150">
      <Header cmsSettings={cmsSettings} />
      <main className="flex-1 min-h-[80vh] pt-20 bg-background page-transition-enter">
        <Outlet />
      </main>
      <Footer cmsSettings={cmsSettings} />
    </div>
  );
};

export default Layout;
